import { useTranslation } from 'react-i18next';

import { isAllowed } from '@hotjar/feature-flags';
import { useCurrentSite } from '@hotjar/site';
import type { Site } from '@hotjar/site';
import { abbrNumber } from '@hotjar/insights-common/lib/number';
import { QuantzHjEventNameValue } from '@hotjar/quantz';
import type { IconName } from '@hotjar/hotsauce';

import { useGetPageRageClicksOrUTurns } from '../../hooks/useGetPageRageClicksOrUTurns';
import type { AggregatePageInsightsComponentProps } from '../../constants-types/AggregatePageInsights';
import { SmartPanelSegment } from './SmartPanelBlocks';
import { SmartPanelSpacedText } from './SmartPanelSpacedText';

type RageClicksOrUTurnSegmentProps = AggregatePageInsightsComponentProps;

type TRageClicksOrUTurns = 'rage-clicks' | 'u-turns';

interface RageClicksOrUTurnsProps extends RageClicksOrUTurnSegmentProps {
  currentSite: Site;
  type: TRageClicksOrUTurns;
}

function RageClicksOrUTurns({
  type,
  selectedDateRange,
  selectedTargetingValue,
  selectedDeviceType,
  searchPattern,
  currentSite,
  isDisabled,
  disabledTooltipText,
}: RageClicksOrUTurnsProps): JSX.Element | null {
  let featureName = 'filters.rage_click';
  let eventType = QuantzHjEventNameValue.RAGE_CLICK;
  let icon: IconName = 'rage_click';
  let translationKey = 'smart-panel.rage-clicks';
  let className = 'hj-hm-sp-rage-clicks';

  if (type === 'u-turns') {
    featureName = 'filters.u_turn';
    eventType = QuantzHjEventNameValue.U_TURN;
    icon = 'uturn';
    translationKey = 'smart-panel.u-turns';
    className = 'hj-hm-sp-u-turns';
  }

  const { t } = useTranslation('heatmaps');
  const isFeatureAllowed = isAllowed(featureName, currentSite);
  const count = useGetPageRageClicksOrUTurns({
    eventType,
    selectedDateRange,
    selectedTargetingValue,
    selectedDeviceType,
    searchPattern,
    siteId: currentSite.id,
    enabled: Boolean(
      !isDisabled &&
        isFeatureAllowed &&
        selectedDateRange !== undefined &&
        selectedTargetingValue &&
        selectedDeviceType &&
        searchPattern,
    ),
  });

  if (!isFeatureAllowed) return null;

  const countText = count !== undefined ? `${abbrNumber(count)}` : '-';

  return (
    <SmartPanelSegment className={className} data-testid={className}>
      <SmartPanelSpacedText
        leftText={t(translationKey)}
        rightText={countText}
        leftIcon={icon}
        isDisabled={isDisabled}
        disabledTooltipText={disabledTooltipText}
      />
    </SmartPanelSegment>
  );
}

export function RageClicksAndUTurnsSegments({
  selectedDateRange,
  selectedTargetingValue,
  selectedDeviceType,
  searchPattern,
  isDisabled,
  disabledTooltipText,
}: RageClicksOrUTurnSegmentProps): JSX.Element | null {
  const { data: currentSite } = useCurrentSite();

  if (currentSite && currentSite.id) {
    return (
      <>
        <RageClicksOrUTurns
          type="rage-clicks"
          selectedDateRange={selectedDateRange}
          selectedTargetingValue={selectedTargetingValue}
          selectedDeviceType={selectedDeviceType}
          searchPattern={searchPattern}
          currentSite={currentSite}
          isDisabled={isDisabled}
          disabledTooltipText={disabledTooltipText}
        />
        <RageClicksOrUTurns
          type="u-turns"
          selectedDateRange={selectedDateRange}
          selectedTargetingValue={selectedTargetingValue}
          selectedDeviceType={selectedDeviceType}
          searchPattern={searchPattern}
          currentSite={currentSite}
          isDisabled={isDisabled}
          disabledTooltipText={disabledTooltipText}
        />
      </>
    );
  }

  return null;
}
